import React from 'react';
import { Link } from 'react-router-dom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ClosingCTA, Container, Eyebrow, PageHero, Section, sectionHeadingClass } from '../components/ui/Primitives';
import { Seo } from '../components/seo/Seo';
import { images, principles } from '../data/site';
import { pageSeo } from '../data/seo';
import { breadcrumbSchema, legalServiceSchema, organizationSchema, webPageSchema } from '../utils/schema';
const seo = pageSeo.about;
const crumbs = [{
  label: 'Home',
  path: '/'
}, {
  label: 'About',
  path: '/about'
}];
export function About() {
  return <>
      <Seo title={seo.title} description={seo.description} path={seo.path} keywords={seo.keywords} image={images.portrait} schemas={[organizationSchema, legalServiceSchema, webPageSchema({
      ...seo
    }), breadcrumbSchema(crumbs)]} data-id="element-120" data-mp="2s9v8noqafepj" />

      <PageHero eyebrow="About" title="About NexLegal" subtitle="A law firm shaped by the belief that legal services can be more modern, more structured, and more accessible." crumbs={crumbs} data-id="element-121" data-mp="1uhidfb8a0e42" />

      <Container className="pt-14 md:pt-20" data-id="element-122" data-mp="23jj7y0c4km21">
        <figure className="overflow-hidden rounded-sm bg-sand" data-id="element-123" data-mp="23g3sgwejhb03">
          <img src={images.hero} alt="The calm, light-filled modern office environment behind NexLegal" width={1600} height={900} loading="lazy" decoding="async" className="aspect-[3/2] w-full object-cover sm:aspect-[16/9]" data-id="element-124" data-mp="1yiluu25z2i7e" />
        </figure>
      </Container>

      {/* Why NexLegal exists */}
      <Section ariaLabel="Why NexLegal exists" data-id="element-125" data-mp="2i8fflwa9rx24">
        <div className="grid gap-8 md:grid-cols-12 md:gap-16" data-id="element-126" data-mp="2i9qkjzfgbaut">
          <div className="md:col-span-4" data-id="element-127" data-mp="2w7ngqupztf8y">
            <Eyebrow data-id="element-128" data-mp="2r82jqpudvgyv">Why NexLegal exists</Eyebrow>
            <h2 className="sr-only" data-id="element-129" data-mp="2r9diuccvw0jn">Why NexLegal exists</h2>
          </div>
          <div className="md:col-span-8" data-id="element-130" data-mp="1ovfq8dyuffvt">
            <p className="font-display text-[26px] font-medium leading-[1.14] tracking-tight text-ink sm:text-[30px] md:text-[40px]" data-id="element-131" data-mp="1owhczfp81qtd">
              Traditional legal services can feel slow, complex, and difficult to
              access.
            </p>
            <p className="mt-7 max-w-prose text-[16.5px] leading-relaxed text-graphite md:mt-8 md:text-[17px]" data-id="element-132" data-mp="22mb2m08jwf45">
              NexLegal was created to explore a better path — combining legal expertise
              with technology and new service models. The result is a firm that keeps
              professional judgment at the centre while removing the friction that has
              long surrounded it.
            </p>
          </div>
        </div>
      </Section>

      {/* Our approach */}
      <section className="border-y border-line bg-sand/60" data-id="element-133" data-mp="m8b2op9pgpgw">
        <Container className="py-20 md:py-28 lg:py-32" data-id="element-134" data-mp="m9vwuds3y6e8">
          <div className="grid gap-10 md:grid-cols-12 md:gap-16" data-id="element-135" data-mp="r7xhh356mdrt">
            <div className="md:col-span-5" data-id="element-136" data-mp="jn3f3oq0f470">
              <Eyebrow data-id="element-137" data-mp="joy0131a6wb3">Our approach</Eyebrow>
              <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-138" data-mp="ewn1a3ia1gk4">
                Four principles we work by.
              </h2>
              <img src={images.portrait} alt="Portrait of a legal professional working in a quiet, daylit office" width={1000} height={1250} loading="lazy" decoding="async" className="mt-10 hidden aspect-[4/5] w-full rounded-sm object-cover md:mt-12 md:block" data-id="element-139" data-mp="9ws6vpzam8ub" />
            </div>
            <ul className="md:col-span-7" data-id="element-140" data-mp="sn0985v64v9g">
              {principles.map((principle, index) => <li key={principle.title} className="border-b border-line py-7 first:border-t md:py-8" data-id="element-141" data-mp="olepq1o4piqa" data-mpi={index}>
                  <div className="flex items-baseline gap-4 sm:gap-5" data-id="element-142" data-mp="39coxy0zeuhf9">
                    <span className="font-display text-[13px] tracking-[0.14em] text-navy" data-id="element-143" data-mp="39bdvq5lm5hq9">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div data-id="element-144" data-mp="15vh8pws5q10h">
                      <h3 className="font-display text-[21px] font-medium tracking-tight text-ink sm:text-[23px]" data-id="element-145" data-mp="15wsdnzxc9et6">
                        {principle.title}
                      </h3>
                      <p className="mt-2 max-w-prose text-[15.5px] leading-relaxed text-graphite md:text-[16px]" data-id="element-146" data-mp="w55spbv781d8">
                        {principle.body}
                      </p>
                    </div>
                  </div>
                </li>)}
            </ul>
          </div>
        </Container>
      </section>

      {/* Future of legal services */}
      <Section ariaLabel="The future of legal services" data-id="element-147" data-mp="1eucex8v7bb3s">
        <div className="grid gap-10 md:grid-cols-12 md:items-center md:gap-16" data-id="element-148" data-mp="3vk1392vj6v92">
          <figure className="md:col-span-5" data-id="element-149" data-mp="3vm42mdikq0qg">
            <img src={images.architecture} alt="Repeating architectural geometry in warm daylight, suggesting structure and order" width={1000} height={1250} loading="lazy" decoding="async" className="aspect-[4/3] w-full rounded-sm object-cover md:aspect-[4/5]" data-id="element-150" data-mp="3gqid2n0i788" />
          </figure>
          <div className="md:col-span-7" data-id="element-151" data-mp="v99usx4p4zi">
            <Eyebrow data-id="element-152" data-mp="wby0t32gtbq">The future of legal services</Eyebrow>
            <h2 className={`mt-6 md:mt-7 ${sectionHeadingClass}`} data-id="element-153" data-mp="39b2qr8vdgih3">
              The future of legal services.
            </h2>
            <p className="mt-6 font-serif text-[21px] font-light leading-[1.36] text-ink sm:text-[24px] md:text-[30px]" data-id="element-154" data-mp="34du3wuza3q9i">
              NexLegal represents a new direction in legal services — one that embraces
              technology while keeping legal judgment, trust, and responsibility at the
              centre.
            </p>
            <div className="mt-8" data-id="element-155" data-mp="2qirrrq3u94o9">
              <Link to="/contact" className="inline-flex items-center gap-2 border-b border-ink pb-1 text-[15px] font-medium text-ink transition-colors hover:border-navy hover:text-navy" data-id="element-156" data-mp="2lmjeki97bljq">
                Get in touch with NexLegal
                <ArrowForwardIcon sx={{
                fontSize: 16
              }} data-id="element-157" data-mp="3k2h6hvzxcl4q" />
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <ClosingCTA headline="Want to know more about NexLegal?" body="We are always glad to talk with people curious about a more modern legal model." data-id="element-158" data-mp="3p0iosepj0gf5" />
    </>;
}